import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { ConfirmParams } from '../components/Confirm/Confirm.state'
import routes from '../routes/routes'
import confirmService from '../services/confirm.service'
import alertService from '../services/alert.service'
import itemService from '../services/item.service'
import storageService from '../services/storage.service'
import customLabelApi, {
  CustomLabelCreateRequest
} from '../apis/customLabel.api'
import itemApi, { ItemData } from '../apis/item.api'
import { ItemType } from '../apis/ItemType.api'
import Card from '../components/Card'
import CardButton from '../components/CardButton'
import InputText from '../components/InputText'
import PageHeading from '../components/PageHeading'
import Row from '../components/Row/Row'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import InputFile from '../components/InputFile'
import { error } from 'console'

interface Props extends RouteComponentProps {}

interface State {
  name: string
  item: string
  csvFilename: string
  apiUrl: string
  itemId: number | null
  items: ItemData[]
  csv: File | null
  isLabelBuilderUser: string
  showPopupError: boolean
  errorMessage: string
}

class CustomLabelCreateForm extends Component<Props, State> {
  initialState: State = {
    name: '',
    item: '',
    csvFilename: '',
    apiUrl: '',
    itemId: null,
    csv: null,
    items: [],
    isLabelBuilderUser: '',
    showPopupError: false,
    errorMessage: ''
  }

  constructor (props: Props) {
    super(props)
    this.state = this.initialState

    this.togglePopupError = this.togglePopupError.bind(this)
    this.handleCancelPopupError = this.handleCancelPopupError.bind(this)
    this.handleSubscribeClick = this.handleSubscribeClick.bind(this)
    this.handleCreateError = this.handleCreateError.bind(this)
  }

  get itemSearchOptions () {
    return itemService.searchOptions(this.state.items)
  }

  togglePopupError = (errorMsg: string) => {
    this.setState((prevState) => ({ showPopupError: !prevState.showPopupError, errorMessage: errorMsg }));
  };

  handleCancelPopupError() {
    this.togglePopupError("")
  }

  handleSubscribeClick() {
    this.togglePopupError("")
    this.props.history.push(routes.userSubscriptions)
  }

  handleCreateError(errorMessage: string) {
    if(errorMessage == "Free account user can only have up to 2 labels.") {
      this.togglePopupError(i18n("Free account user can only have up to 2 labels."));
    } else {
      alertService.error(errorMessage)
    }
  }

  handleInputChange = (name: string, value: string) => {
    if (name === 'name') this.setState({ name: value })
    else if (name === 'item') this.setState({ item: value })
    else if (name === 'apiUrl') this.setState({ apiUrl: value })
  }

  handleItemSearch = (name: string, term: string) => {
    const types: ItemType[] = ['production', 'receivable']
    itemApi.searchByCode(term, types, items => {
      if (!items) return
      this.setState({ items })
    })
  }

  handleItemBlur = () => {
    this.setState({ items: [] })
  }

  handleItemSelect = (name: string, option: SearchPickerOption) => {
    const { value } = option
    const itemId = option.id as number
    const item = itemService.find(this.state.items, itemId)
    if (!item) return
    this.setState({ items: [], item: value, itemId })
  }

  handleFileChange = (name: string, file: File | null, filename: string) => {
    if (name === 'csv') {
      this.setState({
        csv: file,
        csvFilename: filename
      })
    }
  }

  handleCreateConfirm = () => {
    const { name, csv, csvFilename, apiUrl, itemId } = this.state
    const payload: CustomLabelCreateRequest = {}
    if (name) payload.name = name
    if (csv) payload.csv = csv
    if (apiUrl) payload.apiUrl = apiUrl
    if (itemId) payload.item = itemId
    if (csvFilename) payload.csvFilename = csvFilename

    customLabelApi.createWithError(payload, customLabel => {
      if (!customLabel) return
      alertService.success('Successfully created custom label')
      this.props.history.push(routes.customLabels)
    }, error => {
      const { response } = error
      const unauthorized = (response && response.status === 401)
      const requestError = (response && response.data.message)
      const message = requestError || error.message
      if (unauthorized) {
        storageService.remove('authToken')
        window.location.href = routes.signIn
      }
      this.handleCreateError(message)
    })
  }

  handleCreateClick = () => {
    const confirm: ConfirmParams = {
      id: 1,
      question: i18n('Please review custom label details above and then confirm'),
      confirmText: i18n('Confirm'),
      cancelText: i18n('Cancel'),
      onConfirm: () => this.handleCreateConfirm()
    }
    confirmService.push(confirm)
  }

  componentDidMount(): void {
    this.setState({isLabelBuilderUser: storageService.get("isLabelBuilder")})
  }

  render () {

    const isLabelBuilderUser = this.state.isLabelBuilderUser
    const { showPopupError, errorMessage } = this.state;

    return (
      <Card>
        <PageHeading text={i18n("Create Custom Label")} />

        {showPopupError && (
          <>
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={this.handleCancelPopupError}
            ></div>
            <div className="fixed inset-0 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-lg w-80 p-6">
                <h3 className="text-lg font-semibold text-gray-800 mb-4">
                  Subscription Required
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                {errorMessage}
                </p>
                <div className="flex gap-2">
                  <button
                    className="bg-blue-600 text-white px-4 py-2 rounded-md"
                    onClick={this.handleSubscribeClick}
                  >
                    Subscribe
                  </button>
                  <button
                    className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md"
                    onClick={this.handleCancelPopupError}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </>
        )}

        <Row column={2}>
          <InputText
            name="name"
            value={this.state.name}
            label={i18n("Name")}
            onChange={this.handleInputChange} />  
          {!isLabelBuilderUser ? (
            <SearchPicker
            name="item"
            value={this.state.item}
            options={this.itemSearchOptions}
            label={i18n("Item code")}
            onSearch={this.handleItemSearch}
            onChange={this.handleInputChange}
            onSelect={this.handleItemSelect}
            onBlur={this.handleItemBlur} />
          ) : <span></span>}  
          <InputFile
            className="mb-0"
            name="csv"
            filename={this.state.csvFilename}
            accept=".csv"
            label="CSV"
            placeholder="Choose csv"
            onChange={this.handleFileChange} />
          <InputText
            name="apiUrl"
            value={this.state.apiUrl}
            label={i18n("API URL")}
            onChange={this.handleInputChange} />
        </Row>
        <CardButton
          proceedText={i18n("Create")}
          cancelText={i18n("Cancel")}
          onProceedClick={this.handleCreateClick} />
      </Card>
    )
  }
}

export default withRouter(CustomLabelCreateForm)
